import React, { 
  useEffect, 
  useMemo, 
  useRef, 
  useState 
} from "react";
import { v4 as uuidv4 } from "uuid";
import { 
  StewartDialog, 
  StewartDialogActions 
} from "controls/global/dialogs/stewart-dialog/StewartDialog";
import { 
  Box, 
  IconButton, 
  Typography, 
  styled 
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { 
  borderRadius, 
  colors, 
  fontSize, 
  fontWeight, 
  gradients, 
  iconSize, 
  margin, 
  padding 
} from "theme/defaultStyle";
import ActionButton from "controls/global/action-button";
import { 
  ActionResult,
  FileUploadAdditionalInfo, 
  FileUploadStatus, 
  Message 
} from "entities/UIModel";
import useFormWrapper from "utils/custom-hooks/useFormWrapper";
import { TitleMessageUI } from "entities/UIModel/TitleMessageUI";
import TextInputField from "controls/global/text-input-field/TextInputField";
import SendMessageIcon from "theme/icons/SendMessageIcon";
import { FileWithPath } from "file-selector";
import { FileRejection } from "react-dropzone";
import UploadStatusGrid from "./upload-files/UploadStatusGrid";

import { 
  ActionResultStatus,
  DocumentType, 
  MessageType, 
  ProgressImageType, 
  UIConstants
} from "utils/data/enum";
import UploadDropZone from "controls/global/upload-files/UploadDropZone";
import ConfirmationDialogWithProgressbar from "controls/global/dialogs/confirmation-dialog-with-progressbar";
import { useProcessStatusTracking } from "utils/context/ProcessStatusContext";
import { 
  getFormattedErrorMessage, 
  mapFileUploaResponse 
} from "pages/file/utils/fileUploadHelper";
import { useDocument } from "utils/context/DocumentContext";
import ConfirmationDialogV3 from "controls/global/dialogs/confirmation-dialog/ConfirmationDialogV3";
import { useMessage } from "utils/context/TitleMessageContext";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";
import { Document } from "entities/UIModel";
import { useWatch } from "react-hook-form";
import { showToast } from "controls/global/stewart-toast/showToast";
import { useAutomaticProgressDialogActions } from "utils/context/AutomaticProgressDialogContext";
// import { generateRandomNumberId } from "utils/services/utilsService";


const StyledDialog = styled(StewartDialog)({
  "& > .MuiDialog-container > .MuiDialog-paper": {
    maxHeight: "calc(95vh)",
    maxWidth: "calc(50vw)",
    minWidth: "calc(45vw)",
    display: "flex",
    justifyContent: "flex-start",
    padding: "36px 30px 0px 40px",
    overflowX: "auto",
  },
});

const StyledDialogTitle = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  columnGap: "24px",
  "& .MuiTypography-root": {
    fontSize: fontSize.xlarge2,
    fontWeight: fontWeight.bold2,
  },
});

const IconContainer = styled("div")({
  background: gradients.blueGradient09,
  borderRadius: borderRadius.large,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "56px",
  height: "56px",
  "& .MuiSvgIcon-root": {
    color: colors.white,
    fontSize: iconSize.medium2,
  },
});

const StyledDialogAction = styled(StewartDialogActions)({
  justifyContent: "right",
  paddingBottom: padding.xlarge22,
  paddingRight: padding.zero,
  paddingLeft: padding.zero,
});

const CloseIconContainer = styled("div")({
  background: colors.grey10,
  borderRadius: borderRadius.xlarge1,
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  marginLeft: "auto",
  marginBottom: "auto",
  width: "32px",
  height: "32px",
  padding: padding.xsmall1,
  "& .MuiSvgIcon-root": {
    color: colors.blue01,
    width: "24px",
    height: "24px",
  },
  "& .iconButton": {
    padding: padding.zero,
    backgroundColor: colors.grey10,
  },
});

const UploadHeading = styled(Typography)({
  fontSize: fontSize.large,
  fontWeight: fontWeight.bold1,
  textTransform: "capitalize",
});

const ContentWrapper = styled(Box)({
  borderRadius: borderRadius.medium,
  padding: padding.medium1,
  marginTop: margin.xlarge1,
  maxWidth: "100%",
  position: "relative",
  // minHeight: "200px",
});

interface MessageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  message?: (TitleMessageUI & { isToggled?: boolean }) | null;
  isToggled?: boolean;
  onToggle?: (rowId: string) => void;
  key: string;
  notDisabled?: boolean;
  productIndex?: number;
  parentIntegrationKey ? :string;
  performSearchDocument?: () => Promise<void>;
}

// const UPLOAD_FAIL_TITLE = "Document(s) upload failed!";

const SendMessageDocumentDialog = ({ isOpen, onClose, key = "key", notDisabled, productIndex ,parentIntegrationKey, performSearchDocument }: MessageDialogProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const { getValues, setValue } = useFormWrapper();
  const [, { openAutomaticProgressDialog, closeAutomaticProgressDialog }] = useAutomaticProgressDialogActions();
  const [fileUploadStatuses, setFileUploadStatuses] = useState<FileUploadStatus[]>([]);
  const [{ profileSettings }] = useProfileSettingsCache();
  const SUBMIT_SUCCESS = "Message/documents submitted successfully!";
  const SUBMIT_FAILED = "Message/documents submission failed!";
  const isProgressCompletedRef = useRef<boolean>(false);
  const successUploadFilesRef = useRef<FileUploadStatus[]>([]);
  const failedUploadFilesRef = useRef<FileUploadStatus[]>([]);

  const internalFileId = useRef<number>(0);

  // upload files
  const [, { uploadFiles, deleteDocument }] = useDocument()

  const uploadSessionRef = useRef<string>("");
  const [uploadCompleted, setUploadCompleted] = useState<boolean>(false);

  const [, { setBypassDefaultLoader, setPercentageComplete }] = useProcessStatusTracking();
  const [openProgressDialog, setOpenProgressDialog] = useState<boolean>(false);
  const [runProgress, setRunProgress] = useState<boolean>(false);
  const [progressRequestId, setProgressRequestId] = useState<string>("");
  const [hasFailedUpload, setHasFailedUpload] = useState<boolean>(false);
  const [isConfirmatinDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const tobeDeletedDocumentId = useRef<number | undefined>(undefined);
  const [, { sendMessageDoc ,deleteDocuments }] = useMessage();
  const [sending, setSending] = useState(false);
  const messageBodyText = useWatch({
    name: `titleSearchProducts.${productIndex}.messageText`
  });
  const DEFAULT_MESSAGE = "Delete";

  function handleOnClose() {
    onClose && onClose();
  }

  const isSendDisabled = useMemo(() => {
    const trimmed = messageBodyText?.trim() || "";
    return (
      (trimmed === "" && fileUploadStatuses.length === 0) ||
      trimmed.length > 2500
    );
  }, [messageBodyText, fileUploadStatuses]);
  
  const handleOnCloseProgressDialog = async () => {
    isProgressCompletedRef.current = true;
    if (uploadCompleted) {
      setOpenProgressDialog(false);
      setRunProgress(false);
      setProgressRequestId("");
    }
  };

const handleClickOnDone = async () => {
  onClose && onClose();
  const uploadedDocs = successUploadFilesRef.current
  const documentIdsToDelete = uploadedDocs
    .filter((doc) => doc.isUploadSuccess && doc.documentId)
    .map((doc) => doc.documentId);
  if (documentIdsToDelete.length > 0) {
    try {
      await deleteDocuments(documentIdsToDelete);
    } catch (err) {
      console.error("Failed to delete uploaded documents:", err);
    }
  }

  successUploadFilesRef.current = [];
  failedUploadFilesRef.current = [];
  setFileUploadStatuses([]);
  setOpenProgressDialog(false); 
};

  const deleteUploadedImage = async () => {
    if (tobeDeletedDocumentId.current) {
      await deleteDocument(tobeDeletedDocumentId.current);
  
      // Update both ref and state
      successUploadFilesRef.current = successUploadFilesRef.current.filter(
        (f) => f.documentId !== tobeDeletedDocumentId.current
      );
  
      setFileUploadStatuses((prev) =>
        prev.filter((f) => f.documentId !== tobeDeletedDocumentId.current)
      );
    }
  
    setConfirmationDialogOpen(false);
  };
  

  const closeConfirmationDialog = () => {
    setConfirmationDialogOpen(false);
    tobeDeletedDocumentId.current = undefined;
  }

  const handleDialogCancel = () => {
    setConfirmationDialogOpen(false);
    tobeDeletedDocumentId.current = undefined;
  };

  const handleOnUpload = async (acceptedFiles: FileWithPath[], rejectedFiles: FileRejection[]) => {
    const newStatuses: FileUploadStatus[] = acceptedFiles.map((file, index) => ({
      documentId: Date.now() + index,
      documentOriginalFileName: file.name,
      errorMessage: "",
      isUploadSuccess: true,
      documentIndex: index,
    }));

    // setFileUploadStatuses((prev) => [...prev, ...newStatuses]);
    // -- Show upload in progress
    setUploadCompleted(false);
    const progressRequestId = uuidv4();
    setProgressRequestId(progressRequestId);
    setBypassDefaultLoader(true);
    setOpenProgressDialog(true);
    setRunProgress(true);

    isProgressCompletedRef.current = false;

    // Save file if file has not saved yet
    const fileId: number = getValues("id") || 0;
    const orderId: number = getValues(`titleSearchProducts.${productIndex}.orderID`) || 0;
    const processedStatusTypeCode: string = getValues(`titleSearchProducts.${productIndex}.processedStatusTypeCode`);

    // -- Prepare for upload
    let fileAdditionalInfos: FileUploadAdditionalInfo[] = [];
    const formData = new FormData();

    const agency = getValues("agency");
    const companyID = agency?.id || "";
    const clientFileID = getValues("fileNameNumber") || "";
    const companyLegacyID = agency?.legacyID || "";

    formData.set("companyID", companyID);
    formData.set("fileID", fileId.toString());
    formData.set("orderID", orderId.toString());
    formData.set("processedStatusTypeCode", processedStatusTypeCode || "");
    formData.set("uploadSessionKey", uploadSessionRef.current);
    formData.set("clientFileID", clientFileID);
    formData.set("companyLegacyID", companyLegacyID);

    let index = 0;
    acceptedFiles?.forEach((file) => {
      formData.append("files", file, file.name);
      fileAdditionalInfos.push({
        index: index,
        filePath: file.path || "",
        fileName: file.name,
        fileSize: file.size,
        errorMessage: "",
        isValidFile: true,
      });
      index++;
    });

    rejectedFiles?.forEach((rejectedFile, i) => {
      fileAdditionalInfos.push({
        index: index,
        filePath: "", // not available
        fileName: rejectedFile.file.name,
        fileSize: rejectedFile.file.size,
        errorMessage: getFormattedErrorMessage(rejectedFile.errors),
        isValidFile: false,
      });
      index++;
    });

    formData.append("fileUploadAdditionalInfo", JSON.stringify(fileAdditionalInfos));

    // -- Start upload files
    // const uploadedResult = await uploadFiles(progressRequestId, formData, DocumentType.TITLEDOCS); // DocumentType.TITLESEARCHDOC);
    const actionResult:ActionResult = await uploadFiles(progressRequestId, formData, DocumentType.TITLEDOCS); // DocumentType.TITLESEARCHDOC);
  
    if (actionResult.status === ActionResultStatus.Success) {
      setFileUploadStatuses((prev) => [...prev, ...newStatuses]);      

      const uploadedResult = actionResult.apiResponse;
      const fileUploadStatuses = mapFileUploaResponse(uploadedResult);
      // -- show upload result status
      const hasSuccess = fileUploadStatuses?.some((f) => f.isUploadSuccess);
      const hasFailed = fileUploadStatuses?.some((f) => !f.isUploadSuccess);
      successUploadFilesRef.current = [
        ...(successUploadFilesRef.current || []),
        ...fileUploadStatuses.filter((f) => f.isUploadSuccess),
      ];
      const newFailures = fileUploadStatuses.filter((f) => !f.isUploadSuccess);
      if (newFailures.length > 0) {
        failedUploadFilesRef.current = [
          ...(failedUploadFilesRef.current || []),
          ...newFailures,
        ];
        setHasFailedUpload(true);
      }
      // setPercentageComplete(progressRequestId);
      // setUploadCompleted(true);

      if (hasFailed) {
        // TODO show failed grid
        // accumulate failed upload to an array and show them in failed grid
      }
      if (hasSuccess) {
        // TODO show uploaded grid
        // reload DRAFT document on each upload and show them on grid
      }
    }
    else {
      showToast({
        type: 'error',
        title: UIConstants.UNEXPECTED_ERROR_MESSAGE.toString(),
      });  
    }

    setPercentageComplete(progressRequestId);
    setUploadCompleted(true);

  };

  const handleOnDelete = async (documentId: number) => {
    tobeDeletedDocumentId.current = documentId;
    const tobeDeletedFile =
      successUploadFilesRef.current.find((f) => f.documentId === documentId) ??
      fileUploadStatuses.find((f) => f.documentId === documentId); // fallback to state
  
    if (!tobeDeletedFile) {
      setConfirmationMessage(`Delete this document?`);
    } else {
      setConfirmationMessage(
        `${DEFAULT_MESSAGE} '${tobeDeletedFile.documentOriginalFileName}'?`
      );
    }
  
    setConfirmationDialogOpen(true);
  };
  
  const handleSend = async () => {
    openAutomaticProgressDialog(UIConstants.TITLE_SEARCH_SEND_MESSAGE_AND_DOCUMENT_IN_PROGRESS_MESSAGE);
    // handleOnClose()
    
    setSending(true);
    const messageBody = getValues(`titleSearchProducts.${productIndex}.messageText`);
    const messageSubject = getValues(`titleSearchProducts.${productIndex}.messageSubject`) || "";
    const needByDate = getValues(`titleSearchProducts.${productIndex}.needByDate`) || null;
    const myDocuments: Document[] = successUploadFilesRef.current.map(doc => ({
      documentID: doc.documentId,
      documentOriginalFileName: doc.documentOriginalFileName,
      errorMessage: doc.errorMessage,
      isUploadSuccess: doc.isUploadSuccess,
      uniqueIdentifier: "",
      integrationKey : uuidv4(),
    }));
    const documentOnly = (!messageBody || messageBody.trim() === "") && myDocuments.length > 0;
    const orderId = getValues(`titleSearchProducts.${productIndex}.orderID`) || 0;
  
    const scMessage: Message = {
      integrationKey :uuidv4(),
      messageID: 0,
      orderID: orderId,
      messageSubject: messageSubject,
      messageText: messageBody || null,
      messageCreatedByUserID: profileSettings.userID,
      needByDate: needByDate ?? null,
      isSent: 0,
      isHidden: 0,
      forStaffViewOnly: 0,
      fileID :getValues("id"),
      documents: myDocuments,
      messageTypeCode : MessageType.Message,
      isNew : true,
    };
    try {
      if (!sending) {
        await sendMessageDoc(scMessage, documentOnly)        
        performSearchDocument && await performSearchDocument();
        closeAutomaticProgressDialog();
  
        showToast({
          type: "success",
          title: SUBMIT_SUCCESS,
        });
  
        // onClose?.(); // Close only on success
        handleOnClose();
      }
    } catch (err) {
      console.error("Send failed", err);
      closeAutomaticProgressDialog();
  
      showToast({
        type: "error",
        title: SUBMIT_FAILED,
      });
  
    } finally {
      setSending(false);
    }
    
  };
  
  useEffect(() => {
    setOpen(isOpen);
    successUploadFilesRef.current = [];
    failedUploadFilesRef.current = [];
    isProgressCompletedRef.current = false;
  
    if (internalFileId.current) {
      internalFileId.current = getValues("id");
    }
  
    if (isOpen && productIndex !== undefined) {
      setValue(`titleSearchProducts.${productIndex}.messageText`, ""); 
    }
  }, [isOpen]);
  

  useEffect(() => {
    uploadSessionRef.current = uuidv4();
  }, []);

  useEffect(() => {
    // console.log("useEffect - uploadCompleted:", uploadCompleted, "isProgressCompletedRef:", isProgressCompletedRef.current, "openProgressDialog: ", openProgressDialog);
    //if (uploadCompleted && isProgressCompletedRef.current && openProgressDialog) {
    if (uploadCompleted && openProgressDialog) {
      handleOnCloseProgressDialog();
    }
  }, [uploadCompleted]);

  return (
    <>
      <StyledDialog open={open}>
        <StyledDialogTitle
          id="styledDialogTitleDebug"
          sx={{ paddingLeft: "10px" }}
        >
          <IconContainer sx={{ color: "white" }}>
            <SendMessageIcon />
          </IconContainer>
          <Typography id="uploadPolicyImageTitleDebug">Send Message/ Upload Documents</Typography>
          <CloseIconContainer>
            <IconButton
              className="iconButton"
              onClick={handleClickOnDone}
              // size={"large"}
            >
              <CloseIcon />
            </IconButton>
          </CloseIconContainer>
        </StyledDialogTitle>
        <ContentWrapper>
          <TextInputField
            name={`titleSearchProducts.${productIndex}.messageText`}
            multiline
            errorMaxLength={2500}
            errorMessageExceedLimit={"Cannot exceed 2,500 characters"}
            placeholder="Messages"
            label="Messages"
          />
          <Box>
            <UploadDropZone
              handleOnUpload={handleOnUpload}
              documentType={DocumentType.TITLEDOCS}
            />
            {fileUploadStatuses.length > 0 && (
              <Box mt={4}>
                <UploadHeading gutterBottom>Uploaded Documents ({fileUploadStatuses.length})</UploadHeading>
                <UploadStatusGrid
                  fileUploadStatuses={fileUploadStatuses}
                  handleOnDelete={handleOnDelete}
                />
              </Box>
            )}

            {(failedUploadFilesRef.current?.length > 0 || hasFailedUpload) && (
              <Box mt={4}>
                <UploadHeading
                >
                  Failed to Upload ({failedUploadFilesRef.current?.length})
                </UploadHeading>
                <UploadStatusGrid
                  uploadRequestId="failed-1"
                  fileUploadStatuses={failedUploadFilesRef.current}
                />
              </Box>
            )}
          </Box>
          <StyledDialogAction>
            <ActionButton
              variant="outlined"
              color="secondary"
              onClick={handleClickOnDone}
            >
              Cancel
            </ActionButton>
            <ActionButton
              variant="outlined"
              color="primary"
              onClick={handleSend}
              disabled={isSendDisabled}
            >
              Send
            </ActionButton>
          </StyledDialogAction>
        </ContentWrapper>
        <ConfirmationDialogWithProgressbar
          autoClose={true}
          title="Please wait while uploading your documents ..."
          closeButtonText="Done! Click to Continue"
          isOpen={openProgressDialog}
          onClose={handleOnCloseProgressDialog}
          requestId={progressRequestId}
          runProgress={runProgress}
          progressImageType={ProgressImageType.circular}
          delay={1000} // 1000 miliseconds - call processStatus every 1 second 
          maxDelayCounter={90} // max delay: 90 x 1000 miliseconds = 90 seconds
          maxZeroPctCompleteCount={30} //  close 0 percentage dialog after about 30 seconds + response times for each call; where 30 count x delay (1000 miliseconds ) + response for each call
        />
        <ConfirmationDialogV3
          isOpen={isConfirmatinDialogOpen}
          confirmationMessage={confirmationMessage}
          cancelActionButton={true}
          onYes={() => deleteUploadedImage()}
          onNo={() => closeConfirmationDialog()}
          onCancel={handleDialogCancel}
          singleActionButtonText={"Delete"}
          buttonNoText={"Cancel"}
        />
      </StyledDialog>
    </>
  );
};

export default SendMessageDocumentDialog;
